import React, { useState } from 'react';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Tone } from 'components/ScreeningComposer/ComposerParts';
import ViewMore from 'components/ScreeningComposer/ViewMore';
import { riskToneOf } from 'components/ScreeningComposer/riskTone';
import { formatVerificationDate } from './verificationReportSummary';
import VerdictBox, {
  BoxJump,
  BoxSkeleton,
  BoxTrack,
} from 'components/ScreeningComposer/VerdictBox';
import {
  FindingsDrawer,
  BoxDrawer,
} from 'components/Screening/entityScreening/EntityVerdictBoxes';
import 'resources/styles/screening-composer.css';

export interface EntityHubSanctionsInsight {
  /** A new screen is reading the matches; the last evaluation is replaced. */
  evaluating: boolean;
  hasEvaluation: boolean;
  risk: string;
  explanation: string;
  topMatches: string[];
  screenDate?: string;
  matchCount: number;
}

export interface EntityHubOwnershipInsight {
  enabled: boolean;
  settingLoading: boolean;
  running: boolean;
  elapsed: number;
  done: boolean;
  /** The normalized evaluation: summary, risk, counts, checkedAt. */
  info: Record<string, any> | null;
  canRun: boolean;
}

export interface EntityHubMediaInsight {
  running: boolean;
  elapsed: number;
  /** The normalized check: summary, findings, confidence, checkedAt. */
  data: Record<string, any> | null;
  canRun: boolean;
}

interface EntityHubVerdictBoxesProps {
  entityName: string;
  sanctions: EntityHubSanctionsInsight;
  ownership: EntityHubOwnershipInsight;
  media: EntityHubMediaInsight;
  onViewMatches: () => void;
  onRunOwnership: () => void;
  onViewOwnership: () => void;
  onOpenOwnershipSettings: () => void;
  onRunMedia: () => void;
}

const checkedLabel = (value: any) => {
  const date = formatVerificationDate(value ? String(value) : null);
  return date ? `Checked ${date}` : 'Checked';
};

const BoxWaiting: React.FC = () => (
  <>
    <BoxTrack />
    <BoxSkeleton lines={4} />
  </>
);

/**
 * The record's three findings, on the entity screening tool's own boxes:
 * sanctions and denied parties (the AI evaluation), beneficial ownership
 * and adverse media. Each says its verdict, a sentence, and where the
 * evidence is.
 */
const EntityHubVerdictBoxes: React.FC<EntityHubVerdictBoxesProps> = ({
  entityName,
  sanctions,
  ownership,
  media,
  onViewMatches,
  onRunOwnership,
  onViewOwnership,
  onOpenOwnershipSettings,
  onRunMedia,
}) => {
  const [evaluationOpen, setEvaluationOpen] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const name = entityName || 'this entity';

  // Sanctions & denied parties: never green without an evaluation behind it.
  const sanctionsWord = sanctions.hasEvaluation
    ? sanctions.risk
    : 'Not evaluated';
  const sanctionsTone: Tone = sanctions.hasEvaluation
    ? riskToneOf(sanctions.risk)
    : 'none';
  const matchCopy = `${sanctions.matchCount.toLocaleString()} ${
    sanctions.matchCount === 1 ? 'match' : 'matches'
  }`;

  // Beneficial ownership

  // Adverse media
  const findings: any[] = Array.isArray(media.data?.findings)
    ? media.data?.findings || []
    : [];
  const mediaDone = Boolean(media.data);
  const mediaTone: Tone = mediaDone
    ? findings.length
      ? 'medium'
      : 'low'
    : 'none';

  return (
    <>
      <div className='psc-scope psc-boxes entity-hub-boxes'>
        <VerdictBox
          head={{
            glyph: GppMaybeOutlinedIcon,
            glyphTone: sanctions.evaluating ? '' : sanctionsTone,
            label: 'Sanctions & Denied Parties',
            note: 'AI evaluation',
            state: sanctions.evaluating
              ? 'wait'
              : sanctions.hasEvaluation
              ? 'value'
              : 'flat',
            word: sanctionsWord,
            tone: sanctionsTone,
            sub: matchCopy,
          }}
          foot={sanctions.evaluating ? 'Reading the latest matches' : ''}
          actions={
            sanctions.evaluating ? null : (
              <>
                {sanctions.hasEvaluation && (
                  <BoxJump
                    onClick={() => setEvaluationOpen(true)}
                    glyph={ChevronRightIcon}
                  >
                    Full evaluation
                  </BoxJump>
                )}
                <BoxJump onClick={onViewMatches} glyph={ArrowDownwardIcon}>
                  View results
                </BoxJump>
              </>
            )
          }
        >
          {sanctions.evaluating ? (
            <BoxWaiting />
          ) : (
            <ViewMore
              text={
                sanctions.explanation ||
                'Run a screening to generate a contextual risk evaluation for this entity.'
              }
              label='Sanctions and Denied Parties summary'
            />
          )}
        </VerdictBox>

        <VerdictBox
          head={{
            glyph: PeopleOutlineIcon,
            glyphTone: ownership.done ? 'none' : '',
            label: 'Beneficial Ownership',
            note: 'sanctions & affiliates',
            state: ownership.running
              ? 'wait'
              : ownership.done && ownership.enabled
              ? 'value'
              : 'flat',
            waitLabel: 'Checking',
            word: ownership.settingLoading
              ? 'Loading'
              : !ownership.enabled
              ? 'Turned off'
              : ownership.done
              ? 'Checked'
              : 'Not run',
            tone: 'none',
            // No match counts once checked, like the entity screening tool.
            sub: !ownership.enabled
              ? 'In database settings'
              : ownership.done
              ? ''
              : 'No check on file',
          }}
          foot={
            ownership.running
              ? `${ownership.elapsed}s elapsed`
              : ownership.done && ownership.enabled
              ? checkedLabel(ownership.info?.checkedAt)
              : ''
          }
          actions={
            ownership.running ||
            ownership.settingLoading ? null : !ownership.enabled ? (
              <BoxJump
                onClick={onOpenOwnershipSettings}
                glyph={SettingsOutlinedIcon}
                glyphFirst
              >
                Open settings
              </BoxJump>
            ) : ownership.done ? (
              <>
                <BoxJump
                  onClick={onRunOwnership}
                  disabled={!ownership.canRun}
                  glyph={RefreshIcon}
                  ariaLabel='Re-run the beneficial ownership check'
                >
                  Re-run
                </BoxJump>
                <BoxJump onClick={onViewOwnership} glyph={ArrowDownwardIcon}>
                  View results
                </BoxJump>
              </>
            ) : (
              <BoxJump
                onClick={onRunOwnership}
                disabled={!ownership.canRun}
                glyph={SearchIcon}
              >
                Check ownership
              </BoxJump>
            )
          }
        >
          {ownership.running ? (
            <BoxWaiting />
          ) : (
            <ViewMore
              text={
                !ownership.enabled && !ownership.settingLoading
                  ? 'Beneficial Ownership is turned off for this database. A team admin can turn it on in its screening settings to run the 50% rule.'
                  : ownership.info?.summary ||
                    `Traces owners and affiliates of ${name} against the 50% rule. It runs on demand, and the result is saved to this record.`
              }
              label='Beneficial Ownership summary'
            />
          )}
        </VerdictBox>

        <VerdictBox
          head={{
            glyph: ArticleOutlinedIcon,
            glyphTone: mediaDone ? mediaTone : '',
            label: 'Adverse Media',
            note: 'public sources',
            state: media.running ? 'wait' : mediaDone ? 'value' : 'flat',
            waitLabel: 'Checking',
            word: mediaDone
              ? findings.length
                ? 'Findings'
                : 'Clear'
              : 'Not run',
            tone: mediaTone,
            sub: mediaDone
              ? `${findings.length} ${
                  findings.length === 1 ? 'finding' : 'findings'
                }`
              : 'No check on file',
          }}
          foot={
            media.running
              ? `${media.elapsed}s elapsed`
              : mediaDone
              ? checkedLabel(media.data?.checkedAt)
              : ''
          }
          actions={
            media.running ? null : (
              <>
                {mediaDone && (
                  <BoxJump
                    onClick={() => setMediaOpen(true)}
                    glyph={ChevronRightIcon}
                    ariaLabel='Full evaluation of adverse media'
                  >
                    Full evaluation
                  </BoxJump>
                )}
                <BoxJump
                  onClick={onRunMedia}
                  disabled={!media.canRun}
                  glyph={mediaDone ? RefreshIcon : SearchIcon}
                  ariaLabel={
                    mediaDone ? 'Re-run the adverse media check' : undefined
                  }
                >
                  {mediaDone ? 'Re-run' : 'Run check'}
                </BoxJump>
              </>
            )
          }
        >
          {media.running ? (
            <BoxWaiting />
          ) : (
            <ViewMore
              text={
                media.data?.summary ||
                `Searches public news, enforcement actions, litigation and ownership reporting for anything naming ${name}. It runs on demand because it queries outside BITE, and the result is saved to this record.`
              }
              label='Adverse Media summary'
            />
          )}
        </VerdictBox>
      </div>

      <BoxDrawer
        open={evaluationOpen}
        onClose={() => setEvaluationOpen(false)}
        titleId='entity-hub-evaluation-title'
        title='Full evaluation'
        subtitle={`${entityName || 'This entity'} · ${
          sanctions.screenDate || 'Latest screen'
        }`}
        closeLabel='Close full evaluation'
      >
        <div className='es2-evaluation-metrics'>
          <div className='drawer-impact-card'>
            <span className='drawer-impact-label'>Risk</span>
            <span
              className={`drawer-impact-value ${
                sanctionsTone === 'none' ? '' : sanctionsTone
              }`}
            >
              {sanctionsWord}
            </span>
          </div>
          <div className='drawer-impact-card'>
            <span className='drawer-impact-label'>Matches</span>
            <span className='drawer-impact-value'>
              {sanctions.matchCount.toLocaleString()}
            </span>
          </div>
        </div>

        <section>
          <div className='drawer-section-title'>Evaluation summary</div>
          <p className='drawer-text-block es2-evaluation-summary'>
            {sanctions.explanation || 'The evaluation returned no explanation.'}
          </p>
        </section>

        <section>
          <div className='drawer-section-title'>Top matches</div>
          {sanctions.topMatches.length > 0 ? (
            <ol className='entity-hub-top-matches'>
              {sanctions.topMatches.slice(0, 5).map((match, index) => (
                <li key={`${match}-${index}`}>{match}</li>
              ))}
            </ol>
          ) : (
            <p className='drawer-text-block es2-evaluation-empty'>
              No individual top matches were returned.
            </p>
          )}
        </section>
      </BoxDrawer>

      <FindingsDrawer
        open={mediaOpen}
        onClose={() => setMediaOpen(false)}
        entityName={entityName}
        checkedOn={checkedLabel(media.data?.checkedAt)}
        adverseMedia={media.data}
      />
    </>
  );
};

export default EntityHubVerdictBoxes;
